import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useCustomerGroups, usePriceLists, useSalesChannels } from '../../hooks';
import type { BatchAdjustmentType, CreatePriceChangeBatchRequest } from '../../model/priceBatchTypes';
import type { Product } from '../../model/types';
import { ProductMultiPicker } from './ProductMultiPicker';

type TargetType = 'base' | 'channel' | 'group' | 'list';

export interface PriceChangeBatchWizardDialogProps {
  open: boolean;
  /**
   * Preselects the product picker (design spec §8.13, plan-merchant-portal-018 WP2.4) — the
   * products-list "Reprice selected" entry point seeds the merchant's selection here; the chips
   * stay fully editable (a helper caption explains the prefill).
   */
  initialProducts?: Product[];
  /** Preselects the "Target price list" radio (the list-detail "Reprice into list" entry point). */
  initialTargetListCode?: string;
  /** Preselects the "Target sales channel" radio (design spec §8.13 — the products-list bulk entry
   * point, opened from a `channel:` scope). */
  initialTargetChannelCode?: string;
  /** Preselects the "Target customer group" radio (design spec §8.13 — the products-list bulk
   * entry point, opened from a company scope with a customer group selected). */
  initialTargetGroupCode?: string;
  /**
   * A ready-made, already-translated label for whichever `initial*Code` prop above is set (e.g. a
   * channel's name, "List: {name}", or "Group: {name}") — rendered as an informational note on the
   * Target step (design spec §8.13, `bulkBatchesPanel.wizard.targetFromScope`: "target prefilled
   * from the current scope: {target}"). `undefined` when no target was prefilled (the plain
   * "Reprice into list" entry point already explains itself via {@link targetInfoNote} instead).
   */
  initialTargetLabel?: string;
  /**
   * An additional info line shown under the Target step (design spec §8.13) — used by the
   * products-list entry point when it was opened from a `store:` scope whose store has no active
   * price-list binding, to explain why the prefilled target fell back to the base price.
   */
  targetInfoNote?: string;
  pending?: boolean;
  onClose: () => void;
  onSubmit: (body: CreatePriceChangeBatchRequest) => void;
}

/**
 * The bulk-repricing-batch create wizard (design spec §8.8) — a 3-step linear `Stepper`:
 * products (explicit `productIds[]` only — no file upload / whole-category selection, research
 * R12), adjustment (PERCENT/ABSOLUTE/SET), target (the "where to apply" radios — mutually
 * exclusive by construction: base XOR channel XOR group XOR a target price list, adr-pricing-025
 * decision 6).
 */
function initialTargetType(
  initialTargetListCode?: string,
  initialTargetChannelCode?: string,
  initialTargetGroupCode?: string,
): TargetType {
  if (initialTargetListCode) return 'list';
  if (initialTargetChannelCode) return 'channel';
  if (initialTargetGroupCode) return 'group';
  return 'base';
}

export function PriceChangeBatchWizardDialog({
  open,
  initialProducts,
  initialTargetListCode,
  initialTargetChannelCode,
  initialTargetGroupCode,
  initialTargetLabel,
  targetInfoNote,
  pending = false,
  onClose,
  onSubmit,
}: PriceChangeBatchWizardDialogProps) {
  const { t } = useTranslation('catalog');
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState('');
  const [products, setProducts] = useState<Product[]>(initialProducts ?? []);
  const [adjustmentType, setAdjustmentType] = useState<BatchAdjustmentType>('PERCENT');
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [targetType, setTargetType] = useState<TargetType>(
    initialTargetType(initialTargetListCode, initialTargetChannelCode, initialTargetGroupCode),
  );
  const [targetChannelCode, setTargetChannelCode] = useState(initialTargetChannelCode ?? '');
  const [targetGroupCode, setTargetGroupCode] = useState(initialTargetGroupCode ?? '');
  const [targetListCode, setTargetListCode] = useState(initialTargetListCode ?? '');

  const channelsQuery = useSalesChannels();
  const listsQuery = usePriceLists();
  const groupsQuery = useCustomerGroups();
  const channels = (channelsQuery.data ?? []).filter((c) => c.active);
  const lists = (listsQuery.data ?? []).filter((l) => l.active);
  const groups = (groupsQuery.data ?? []).filter((g) => g.active);

  const reset = () => {
    setActiveStep(0);
    setName('');
    setProducts(initialProducts ?? []);
    setAdjustmentType('PERCENT');
    setAdjustmentValue('');
    setTargetType(initialTargetType(initialTargetListCode, initialTargetChannelCode, initialTargetGroupCode));
    setTargetChannelCode(initialTargetChannelCode ?? '');
    setTargetGroupCode(initialTargetGroupCode ?? '');
    setTargetListCode(initialTargetListCode ?? '');
  };

  // Re-seed from the LATEST `initial*` props every time the dialog opens (design spec §8.13,
  // plan-merchant-portal-018 WP2.4) — mirrors `QuickStockDialog`'s own `useEffect(() => { if
  // (open) {...} }, [open, ...])` idiom in this same file's sibling module. The products-list
  // caller (`ProductsPage`) keeps this dialog mounted continuously (so MUI's own close transition
  // still plays), passing a FRESH `initialProducts`/`initialTarget*` on every render; without this
  // effect the `useState(initialProducts ?? [])` initializers above would only ever see the value
  // from this component's very FIRST mount (typically `[]`, before any selection existed), silently
  // reopening the wizard with an empty product picker no matter what the merchant had selected.
  // `reset` is intentionally excluded from the deps array (it is redefined every render); the
  // effect itself only re-fires on `open`, and by then `reset`'s closure already sees the props
  // from that same render.
  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (pending) return;
    reset();
    onClose();
  };

  const parsedValue = Number.parseFloat(adjustmentValue);
  const stepValid = [
    products.length > 0,
    adjustmentValue.trim() !== '' && !Number.isNaN(parsedValue) && (adjustmentType !== 'SET' || parsedValue > 0),
    targetType === 'base' ||
      (targetType === 'channel' && targetChannelCode !== '') ||
      (targetType === 'group' && targetGroupCode !== '') ||
      (targetType === 'list' && targetListCode !== ''),
  ];
  const isLastStep = activeStep === stepValid.length - 1;

  const handleSubmit = () => {
    onSubmit({
      name: name.trim() || undefined,
      adjustmentType,
      adjustmentValue: parsedValue,
      targetChannelCode: targetType === 'channel' ? targetChannelCode : undefined,
      targetCustomerGroupCode: targetType === 'group' ? targetGroupCode : undefined,
      targetPriceListCode: targetType === 'list' ? targetListCode : undefined,
      productIds: products.map((p) => p.id),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('bulkBatchesPanel.create')}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          <Step>
            <StepLabel>{t('bulkBatchesPanel.wizard.stepProducts')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('bulkBatchesPanel.wizard.stepAdjustment')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('bulkBatchesPanel.wizard.stepTarget')}</StepLabel>
          </Step>
        </Stepper>

        {activeStep === 0 ? (
          <Stack spacing={2.5}>
            <TextField
              label={t('bulkBatchesPanel.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <ProductMultiPicker value={products} onChange={setProducts} />
            {initialProducts && initialProducts.length > 0 ? (
              <Alert severity="info" icon={false}>
                {t('bulkBatchesPanel.wizard.prefilledProducts')}
              </Alert>
            ) : null}
          </Stack>
        ) : null}

        {activeStep === 1 ? (
          <Stack spacing={2.5}>
            <RadioGroup
              value={adjustmentType}
              onChange={(e) => setAdjustmentType(e.target.value as BatchAdjustmentType)}
            >
              <FormControlLabel value="PERCENT" control={<Radio />} label={t('bulkBatchesPanel.wizard.adjustmentPercent')} />
              <FormControlLabel value="ABSOLUTE" control={<Radio />} label={t('bulkBatchesPanel.wizard.adjustmentAbsolute')} />
              <FormControlLabel value="SET" control={<Radio />} label={t('bulkBatchesPanel.wizard.adjustmentSet')} />
            </RadioGroup>
            <TextField
              label={
                adjustmentType === 'PERCENT'
                  ? t('bulkBatchesPanel.wizard.valuePercentLabel')
                  : t('bulkBatchesPanel.wizard.valueAmountLabel')
              }
              type="number"
              value={adjustmentValue}
              onChange={(e) => setAdjustmentValue(e.target.value)}
              helperText={adjustmentType === 'SET' ? t('bulkBatchesPanel.wizard.valueSetHelper') : t('bulkBatchesPanel.wizard.valueSignedHelper')}
              slotProps={{ htmlInput: { step: '0.01' } }}
              sx={{ maxWidth: 280 }}
            />
          </Stack>
        ) : null}

        {activeStep === 2 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('bulkBatchesPanel.targetLabel')}
            </Typography>
            <RadioGroup value={targetType} onChange={(e) => setTargetType(e.target.value as TargetType)}>
              <FormControlLabel value="base" control={<Radio />} label={t('bulkBatchesPanel.target.base')} />
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FormControlLabel value="channel" control={<Radio />} label={t('bulkBatchesPanel.target.channel')} />
                <TextField
                  select
                  size="small"
                  disabled={targetType !== 'channel'}
                  value={targetChannelCode}
                  onChange={(e) => setTargetChannelCode(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {channels.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              {groups.length > 0 ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <FormControlLabel value="group" control={<Radio />} label={t('bulkBatchesPanel.target.group')} />
                  <TextField
                    select
                    size="small"
                    disabled={targetType !== 'group'}
                    value={targetGroupCode}
                    onChange={(e) => setTargetGroupCode(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {groups.map((g) => (
                      <MenuItem key={g.code} value={g.code}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ) : null}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FormControlLabel value="list" control={<Radio />} label={t('bulkBatchesPanel.target.list')} />
                <TextField
                  select
                  size="small"
                  disabled={targetType !== 'list'}
                  value={targetListCode}
                  onChange={(e) => setTargetListCode(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {lists.map((l) => (
                    <MenuItem key={l.code} value={l.code}>
                      {l.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </RadioGroup>
            {targetType === 'list' ? (
              <Alert severity="info" icon={false}>
                {t('bulkBatchesPanel.targetListHelper')}
              </Alert>
            ) : null}
            {/* design spec §8.13 — names which target the products-list entry point prefilled
                from the current scope; the merchant can still change it above. */}
            {initialTargetLabel ? (
              <Alert severity="info" icon={false}>
                {t('bulkBatchesPanel.wizard.targetFromScope', { target: initialTargetLabel })}
              </Alert>
            ) : null}
            {targetInfoNote ? <Alert severity="info">{targetInfoNote}</Alert> : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        {activeStep > 0 ? (
          <Button onClick={() => setActiveStep((s) => s - 1)} disabled={pending}>
            {t('common:action.back')}
          </Button>
        ) : null}
        {isLastStep ? (
          <Button
            variant="contained"
            disabled={!stepValid.every(Boolean) || pending}
            startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={handleSubmit}
          >
            {t('bulkBatchesPanel.wizard.createAndDryRun')}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!stepValid[activeStep]}
            onClick={() => setActiveStep((s) => s + 1)}
          >
            {t('bulkBatchesPanel.wizard.nextAction')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
